// Collections page. List + upload + delete a user's MTG card collections.
//
// Upload flow: paste a Manabox / Moxfield / Archidekt / Deckbox / Dragon
// Shield / Untapped.gg CSV (or any plain-text cardlist) into the textarea,
// or drop a file.
// Note: "cardlist" intentional — collections are owned cards, not 60-card decks.
// Auto-detect picks the right parser; the result row shows up in the list
// the moment the request returns. Hooked to /api/collections (requireAuth).
//
// Build-with-collection lives on /build — the picker there reads the same
// list endpoint and threads `collection_id` into the build SSE call.

import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Library, Trash2, Upload, X } from "lucide-react";
import {
  createCollectionFromCsv,
  deleteCollection,
  listCollections,
  type CollectionSource,
  type CollectionSummary,
} from "../lib/api";
import LiquidButton from "../components/ui/LiquidButton";
import MagicButton from "../components/ui/MagicButton";
import { cn } from "../lib/cn";

const MAX_CSV_BYTES = 5 * 1024 * 1024;

const SOURCE_LABEL: Record<CollectionSource, string> = {
  manabox: "Manabox",
  moxfield: "Moxfield",
  archidekt: "Archidekt",
  deckbox: "Deckbox",
  dragonshield: "Dragon Shield",
  untapped: "Untapped.gg",
  plain_text: "Plain text",
  manual: "Manual",
};

export default function Collections() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState("");
  const [csv, setCsv] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [lastResult, setLastResult] = useState<{
    source: string;
    card_count: number;
    oracle_count: number;
    unresolved_count?: number;
  } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["collections"],
    queryFn: listCollections,
  });

  const collections = data?.collections ?? [];

  const create = useMutation({
    mutationFn: async () => {
      if (!csv.trim()) throw new Error("paste a CSV or cardlist first");
      if (csv.length > MAX_CSV_BYTES) {
        throw new Error(`CSV exceeds ${MAX_CSV_BYTES} bytes`);
      }
      return createCollectionFromCsv(csv, name.trim() ? { name: name.trim() } : {});
    },
    onSuccess: (res) => {
      setError(null);
      setWarnings([]);
      setLastResult({
        source: res.source,
        card_count: res.card_count,
        oracle_count: res.oracle_count,
        unresolved_count: res.unresolved_count,
      });
      if (Array.isArray(res.warnings) && res.warnings.length > 0) {
        setWarnings(res.warnings);
      }
      setName("");
      setCsv("");
      queryClient.invalidateQueries({ queryKey: ["collections"] });
    },
    onError: (e) => {
      setError((e as Error).message);
      setWarnings([]);
      setLastResult(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteCollection(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["collections"] }),
  });

  const onFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";
      if (file.size > MAX_CSV_BYTES) {
        setError(`file exceeds ${MAX_CSV_BYTES} byte limit`);
        return;
      }
      try {
        const text = await file.text();
        setCsv(text);
        if (!name.trim()) setName(file.name.replace(/\.[^./]+$/, ""));
        setError(null);
      } catch (err) {
        setError(`could not read file: ${(err as Error).message}`);
      }
    },
    [name],
  );

  const submitDisabled = create.isPending || !csv.trim();

  return (
    <div className="mx-auto max-w-5xl px-6 md:px-10 py-10 space-y-8">
      <header>
        <div className="eyebrow">Collections</div>
        <h1 className="mt-2 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.0]">
          Build only from
          <br />
          <span className="foil">cards you own.</span>
        </h1>
        <p className="text-sm text-fg-muted mt-5 max-w-2xl leading-relaxed">
          Upload a CSV from Manabox, Moxfield, Archidekt, Deckbox, Dragon
          Shield, or Untapped.gg — or paste a plain-text cardlist. The build
          agent will restrict its candidate pool to your collection and refuse to
          recommend cards you don't have. Basic lands are always available.
        </p>
      </header>

      <CollectionUploadCard
        name={name}
        csv={csv}
        onName={setName}
        onCsv={setCsv}
        onPickFile={() => fileInputRef.current?.click()}
        onSubmit={() => create.mutate()}
        submitting={create.isPending}
        submitDisabled={submitDisabled}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.txt,text/csv,text/plain"
        className="hidden"
        onChange={onFileChange}
      />

      {error && (
        <div className="surface p-4 border-l-4 border-l-rose-500/70 text-sm text-fg">
          <strong className="text-rose-300">Upload failed:</strong> {error}
        </div>
      )}

      {warnings.length > 0 && (
        <div className="surface p-4 border-l-4 border-l-amber-500/70 text-sm text-fg-muted space-y-1">
          <strong className="text-amber-300 block mb-1">Warnings</strong>
          {warnings.map((w, i) => <div key={i}>{w}</div>)}
        </div>
      )}

      {lastResult && !error && (
        <div className="surface p-4 border-l-4 border-l-emerald-500/70 text-sm">
          <strong className="text-emerald-300">Imported.</strong>{" "}
          {SOURCE_LABEL[lastResult.source as CollectionSource] ?? lastResult.source}{" "}
          · {lastResult.oracle_count} unique cards · {lastResult.card_count} total
          {lastResult.unresolved_count
            ? ` · ${lastResult.unresolved_count} unresolved`
            : ""}
        </div>
      )}

      <CollectionList
        loading={isLoading}
        collections={collections}
        onDelete={(id) => {
          if (confirm("Delete this collection? This cannot be undone.")) {
            remove.mutate(id);
          }
        }}
      />
    </div>
  );
}

interface UploadCardProps {
  name: string;
  csv: string;
  onName: (s: string) => void;
  onCsv: (s: string) => void;
  onPickFile: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitDisabled: boolean;
}

function CollectionUploadCard(props: UploadCardProps) {
  return (
    <section className="surface p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Library size={16} className="text-fg-muted" />
        <h2 className="text-base font-medium tracking-tight">New collection</h2>
      </div>

      <div className="space-y-2">
        <label className="text-xs text-fg-muted block">Name (optional)</label>
        <input
          className="input w-full text-sm"
          placeholder="My binder"
          value={props.name}
          onChange={(e) => props.onName(e.target.value)}
          disabled={props.submitting}
        />
      </div>

      <div className="space-y-2">
        <label className="text-xs text-fg-muted block">
          CSV body or plain-text cardlist
        </label>
        <textarea
          className="input min-h-[180px] font-mono text-[13px] resize-y w-full"
          placeholder={
            "Paste a Manabox / Moxfield / Archidekt / Deckbox / Dragon Shield / Untapped.gg CSV — auto-detected by header.\n" +
            "Or paste a plain cardlist:\n4 Lightning Bolt\n4 Goblin Guide (ZEN) 145\n…"
          }
          value={props.csv}
          onChange={(e) => props.onCsv(e.target.value)}
          disabled={props.submitting}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <MagicButton
          variant="ghost"
          onClick={props.onPickFile}
          disabled={props.submitting}
          className="h-10 text-sm"
        >
          <Upload size={14} />
          Choose file
        </MagicButton>
        <div className="flex-1" />
        <LiquidButton
          className="h-10 text-sm"
          disabled={props.submitDisabled}
          onClick={props.onSubmit}
        >
          {props.submitting ? "Importing…" : "Import collection"}
        </LiquidButton>
      </div>
    </section>
  );
}

interface ListProps {
  loading: boolean;
  collections: CollectionSummary[];
  onDelete: (id: string) => void;
}

function CollectionList(props: ListProps) {
  const sorted = useMemo(
    () =>
      [...props.collections].sort(
        (a, b) =>
          new Date(b.imported_at).getTime() - new Date(a.imported_at).getTime(),
      ),
    [props.collections],
  );

  if (props.loading) {
    return (
      <div className="surface p-8 text-center text-sm text-fg-muted">
        Loading collections…
      </div>
    );
  }
  if (sorted.length === 0) {
    return (
      <div className="surface p-8 text-center space-y-3">
        <X size={20} className="mx-auto text-fg-subtle" />
        <div className="text-sm text-fg-muted">
          No collections yet. Paste a Manabox CSV above to get started.
        </div>
      </div>
    );
  }
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-medium tracking-tight text-fg-muted">
        Your collections ({sorted.length})
      </h2>
      <ul className="space-y-2">
        {sorted.map((c) => (
          <li
            key={c.id}
            className={cn(
              "surface p-4 flex flex-wrap items-center gap-3",
              "transition-colors hover:bg-bg-raised/50",
            )}
          >
            <div className="flex-1 min-w-0">
              <div className="font-medium tracking-tight truncate">{c.name}</div>
              <div className="text-xs text-fg-muted mt-1 flex flex-wrap items-center gap-2">
                <span>{SOURCE_LABEL[c.source] ?? c.source}</span>
                <span>·</span>
                <span>{c.oracle_count} unique</span>
                <span>·</span>
                <span>{c.card_count} total</span>
                <span>·</span>
                <span title={new Date(c.imported_at).toLocaleString()}>
                  imported {new Date(c.imported_at).toLocaleDateString()}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => props.onDelete(c.id)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border bg-bg-raised/50 hover:bg-rose-500/15 hover:border-rose-500/40 hover:text-rose-200 text-xs text-fg-muted transition-colors"
              title="Delete collection"
            >
              <Trash2 size={14} />
              Delete
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
